import { normalizeAccessCode } from '../shared/survey';
import type { SurveyKey } from '../shared/survey';
import type { HvyPluginContext } from './hvy-types';

export type AccessState =
  | { status: 'idle' | 'checking' | 'invalid' | 'error'; code: string }
  | { status: 'valid'; code: string; neighborhood: string; surveyId: string; surveyVersion: number; surveyKey: SurveyKey; questionCount: number };

export function createAccessCodePlugin(onStateChanged: (state: AccessState) => void) {
  return {
    id: 'petro-survey.access-code',
    uuid: 'petro-survey-access-code-v1',
    version: '1.0.0',
    hvyApiVersion: '0.1',
    displayName: 'Survey Access Code',
    create(ctx: HvyPluginContext) {
      const element = document.createElement('div');
      element.className = 'access-code-plugin';
      const input = document.createElement('input');
      input.id = 'survey-access-code-input';
      input.className = 'access-code-input';
      input.type = 'text';
      input.inputMode = 'text';
      input.autocomplete = 'off';
      input.autocapitalize = 'characters';
      input.maxLength = 7;
      input.placeholder = 'ABC1234';
      input.setAttribute('aria-label', 'Seven-character access code');
      input.setAttribute('aria-describedby', 'survey-access-code-status');
      const status = document.createElement('p');
      status.id = 'survey-access-code-status';
      status.className = 'access-code-status';
      status.setAttribute('role', 'status');
      element.append(input, status);

      let requestSequence = 0;
      let latestValue = normalizeAccessCode(String(ctx.block.schema.pluginConfig?.value ?? ''));

      const writeState = (state: AccessState) => {
        element.dataset.state = state.status;
        if (state.status === 'valid') status.textContent = state.neighborhood;
        else if (state.status === 'checking') status.textContent = 'Checking code…';
        else if (state.status === 'invalid') status.textContent = 'That access code is not valid.';
        else if (state.status === 'error') status.textContent = 'We could not check the code. Please try again.';
        else status.textContent = state.code ? 'Enter all seven characters.' : '';
        onStateChanged(state);
      };

      const validate = async (code: string) => {
        const sequence = ++requestSequence;
        if (code.length !== 7) {
          writeState({ status: 'idle', code });
          return;
        }
        writeState({ status: 'checking', code });
        try {
          const response = await fetch(`/api/access-codes/${encodeURIComponent(code)}?includeSurvey=true`);
          if (sequence !== requestSequence) return;
          if (!response.ok) {
            writeState({ status: 'invalid', code });
            return;
          }
          const result = await response.json() as Partial<Extract<AccessState, { status: 'valid' }>> & { valid: boolean };
          if (result.valid && result.neighborhood && result.surveyId && result.surveyVersion && result.surveyKey && result.questionCount) {
            writeState({
              status: 'valid',
              code,
              neighborhood: result.neighborhood,
              surveyId: result.surveyId,
              surveyVersion: result.surveyVersion,
              surveyKey: result.surveyKey,
              questionCount: result.questionCount,
            });
          }
          else writeState({ status: 'invalid', code });
        } catch {
          if (sequence === requestSequence) writeState({ status: 'error', code });
        }
      };

      input.value = latestValue;
      input.addEventListener('input', () => {
        const next = normalizeAccessCode(input.value);
        if (input.value !== next) input.value = next;
        if (next === latestValue) return;
        latestValue = next;
        ctx.setConfig({ value: next });
        void validate(next);
      });
      queueMicrotask(() => void validate(latestValue));

      return {
        element,
        refresh() {
          const next = normalizeAccessCode(String(ctx.block.schema.pluginConfig?.value ?? ''));
          if (next === latestValue) return;
          latestValue = next;
          input.value = next;
          void validate(next);
        },
      };
    },
  };
}
